import {
  Home02,
  Logout03,
  User03,
  FaceID,
  Shield02,
  CreditCard01,
  Wallet02,
  FileCheck02,
  MessageCircle01
} from "untitledui-js-base";
import { cx } from "shared/utils";
import { TabStepsType } from "views/applications-id-report";
import {
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { StatusIcon } from "shared/app/VerificationStatusIcon";
import {
  mapCombinedVerificationNameToTitle,
  mapCombinedVerificationToStatus
} from "utils/combineVerifications";
import { Attachment02 } from "untitledui-js-base";
import { useQuery } from "@tanstack/react-query";
import { instance } from "../../../api/apiBase";
import { Select, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { usePostHog } from "posthog-js/react";
import { Application } from "types/application";

const mapTabNameToIcon = (name: TabStepsType | string) => {
  switch (name) {
    case VerificationNames.RentalApplication:
      return <User03 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.IdentityVerification:
      return <FaceID className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return <Wallet02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.HousingHistory:
      return <Home02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.CreditReport:
      return <CreditCard01 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.EvictionReport:
      return <Logout03 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.CriminalReport:
      return <Shield02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.SubmissionDisclosure:
    case VerificationNames.TermsAndConditions:
      return <FileCheck02 className="shrink-0 stroke-inherit" size="20px" />;
    default:
      return;
  }
};

interface SidebarProps {
  applicantId: number;
  application: Application;
  tabSteps: (Verification | Verification[])[];
  currentTab: TabStepsType;
  setCurrentTab: (tabName: TabStepsType) => void;
}

const PAGE_PADDING = 24,
  HEADER_HEIGHT = 180;

export const Sidebar = ({
  applicantId,
  application,
  tabSteps: verifications,
  currentTab,
  setCurrentTab
}: SidebarProps) => {
  const { data } = useQuery({
    queryFn: () =>
      instance
        .get(`/portal/v1/applicants/${applicantId}/additional_documents`)
        .then(({ data }) => data),
    queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
  });

  const { type } = useAuthContext();
  const posthog = usePostHog();
  const showChat = posthog.isFeatureEnabled("chat");

  const isIdentityVerificationOverrideRequired =
    application.requires_overrides_identity_verification.applicants.some(
      ({ id }) => id === applicantId
    );

  return (
    <>
      <div
        className={cx(
          "no-print -top-5 mb-8 pl-2 lg:sticky lg:mb-0 lg:pl-0 lg:pr-4 md:block hidden",
          // h-px is used to keep sticky sidebar sticky
          "lg:h-[500px]"
        )}
      >
        {verifications.map((tabStep: Verification | Verification[]) => {
          const verification = Array.isArray(tabStep) ? tabStep[0] : tabStep;

          const isSelected = currentTab === verification.name;

          const notStartedOrInProgressOrSkipped = [
            VerificationStatus.Pending,
            VerificationStatus.Started,
            VerificationStatus.Skipped
          ].includes(verification.status);

          return (
            <div
              className={cx(
                "my-1 flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg px-3 py-2",
                isSelected && "rounded-lg bg-primary-20 stroke-primary-500",
                notStartedOrInProgressOrSkipped
                  ? "stroke-content-100"
                  : "stroke-content-500 hover:bg-alpha-40"
              )}
              onClick={() => {
                document.getElementById("report_section")?.scrollTo({
                  top: HEADER_HEIGHT + PAGE_PADDING,
                  behavior: "smooth"
                });
                setCurrentTab(verification.name as TabStepsType);
              }}
              key={verification.name}
            >
              <div className="flex flex-shrink items-center gap-2 stroke-inherit">
                {mapTabNameToIcon(verification.name)}
                <p
                  className={cx(
                    "truncate text-sm font-medium",
                    isSelected
                      ? "text-primary-600"
                      : notStartedOrInProgressOrSkipped
                        ? "text-content-100"
                        : "text-content-500"
                  )}
                >
                  {mapCombinedVerificationNameToTitle(tabStep)}
                </p>
              </div>
              <div className="flex items-center justify-center">
                <StatusIcon
                  applicantStatus={
                    application.applicants.find((app) => app.id === applicantId)
                      ?.application_status
                  }
                  isIdentityVerificationOverrideRequired={
                    isIdentityVerificationOverrideRequired
                  }
                  verificationStatus={mapCombinedVerificationToStatus(
                    verification
                  )}
                  isManuallyOverridden={Boolean(verification.overridden_by)}
                  name={verification.name}
                  executionMode={verification.settings?.execution_mode}
                  applicationId={application.id}
                  applicantId={applicantId}
                  verificationId={verification.id}
                />
              </div>
            </div>
          );
        })}

        <div className="border-t border-alpha-40 my-4" />

        <div
          className={cx(
            "my-1 flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg p-3",
            currentTab === "additional_documents"
              ? "rounded-lg bg-primary-20 stroke-primary-500"
              : "stroke-content-500 hover:bg-alpha-40"
          )}
          onClick={() => {
            document.getElementById("report_section")?.scrollTo({
              top: HEADER_HEIGHT + PAGE_PADDING,
              behavior: "smooth"
            });
            setCurrentTab("additional_documents");
          }}
        >
          <div className="flex flex-shrink items-center gap-2 stroke-inherit">
            <Attachment02 className="shrink-0 stroke-inherit" size="20px" />
            <p
              className={cx(
                "truncate text-sm font-medium",
                currentTab === "additional_documents"
                  ? "text-primary-500"
                  : "text-content-500"
              )}
            >
              Additional documents
            </p>
          </div>
          <div className="flex items-center justify-center">
            <p
              className={cx(
                "text-sm font-medium",
                currentTab === "additional_documents"
                  ? "text-primary-600"
                  : "text-content-500"
              )}
            >
              {data?.total_count || 0}
            </p>
          </div>
        </div>

        {/* Chat with applicant */}
        <Show if={type.isBoomScreen && showChat}>
          <div
            className={cx(
              "flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg p-3",
              currentTab === "chat"
                ? "rounded-lg bg-primary-20 stroke-primary-500"
                : "stroke-content-500 hover:bg-alpha-40"
            )}
            onClick={() => {
              document.getElementById("report_section")?.scrollTo({
                top: HEADER_HEIGHT + PAGE_PADDING,
                behavior: "smooth"
              });
              setCurrentTab("chat");
            }}
          >
            <div className="flex flex-shrink items-center gap-2 stroke-inherit">
              <MessageCircle01
                className="shrink-0 stroke-inherit"
                size="20px"
              />
              <p
                className={cx(
                  "truncate text-sm font-medium",
                  currentTab === "chat"
                    ? "text-primary-500"
                    : "text-content-500"
                )}
              >
                Chat
              </p>
            </div>
          </div>
        </Show>
      </div>

      <Select
        id="report-selector"
        className="md:hidden"
        onChange={(e) => {
          setCurrentTab(e.target.value as TabStepsType);
        }}
        value={currentTab}
        options={verifications.map((tabStep: Verification | Verification[]) => {
          const verification = Array.isArray(tabStep) ? tabStep[0] : tabStep;
          return {
            value: String(verification.name),
            label: String(mapCombinedVerificationNameToTitle(tabStep))
          };
        })}
      />
    </>
  );
};
